import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import '../styles/auth.css';

const SetupPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [tokenData, setTokenData] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const token = searchParams.get('token');

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError('確認トークンが見つかりません。');
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`/api/auth/verify-email?token=${token}`);

        if (response.data.token_valid) {
          setTokenData(response.data);
        } else {
          setError(response.data.message);
        }
      } catch (err) {
        setError(
          err.response?.data?.detail ||
          '確認トークンの検証中にエラーが発生しました。'
        );
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, [token]);

  const validatePassword = () => {
    if (!password) {
      setError('パスワードを入力してください。');
      return false;
    }

    if (password.length < 8) {
      setError('パスワードは8文字以上で入力してください。');
      return false;
    }

    if (password !== confirmPassword) {
      setError('パスワードが一致しません。');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validatePassword()) {
      return;
    }

    setError('');
    setSubmitting(true);

    try {
      const response = await axios.post('/api/auth/setup-password', {
        token,
        password
      });

      // Success - redirect to login
      alert(response.data.message);
      navigate('/login');
    } catch (err) {
      setError(
        err.response?.data?.detail ||
        'パスワード設定中にエラーが発生しました。'
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h1>アカウント有効化</h1>
          </div>
          <div className="auth-body">
            <p className="loading-message">確認中...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !tokenData) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h1>アカウント有効化</h1>
          </div>
          <div className="auth-body">
            <div className="error-message">{error}</div>
            <button
              className="btn btn-primary"
              onClick={() => navigate('/login')}
              style={{ marginTop: '20px' }}
            >
              ログインページへ
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>パスワード設定</h1>
        </div>

        <div className="auth-body">
          <div className="welcome-message">
            <p>こんにちは、<strong>{tokenData?.display_name}</strong> 様</p>
            <p className="email-info">{tokenData?.email}</p>
            <p className="info-text">
              アカウントを有効化するため、パスワードを設定してください。
            </p>
          </div>

          {error && <div className="error-message">{error}</div>}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="password">パスワード</label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="8文字以上で入力してください"
                  className="form-control"
                  required
                  disabled={submitting}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex="-1"
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
              <small className="form-text">
                8文字以上で設定してください。
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">パスワード（確認）</label>
              <div className="password-input-wrapper">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="もう一度入力してください"
                  className="form-control"
                  required
                  disabled={submitting}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  tabIndex="-1"
                >
                  {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-block"
              disabled={!password || !confirmPassword || submitting}
            >
              {submitting ? '設定中...' : 'パスワードを設定'}
            </button>
          </form>

          <div className="info-box">
            <p>注意事項</p>
            <ul>
              <li>パスワードは安全に保管してください</li>
              <li>他人と共有しないでください</li>
              <li>定期的な変更を推奨します</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SetupPasswordPage;
